import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import type { IBooking } from '@cnt-workspace/models';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ReservationApiService } from '../backend-api/reservation-api.service';
import { parseBookingDate } from '../shared/iso-date.util';
import { toBookingView } from './reservation-real-mapper';

/** Same eligibility rule as TripsComponent.canReview — the stay actually happened and is over. */
function stayIsOver(b: IBooking): boolean {
  if (b.status === 'cancelled' || b.status === 'declined' || b.status === 'pending') return false;
  const end = parseBookingDate(b.dates.end);
  return !!end && end.getTime() < Date.now();
}

/**
 * Route guard for `/reservation-processing/:id` — the legacy URL the server
 * puts in every reservation email/push (`generateReservationLink`), including
 * the review reminder sent to both parties. Never activates; it always
 * redirects to the cnt-landing page that handles the reservation for the
 * signed-in user's role:
 *  - guest: unreviewed finished stay → trips review modal, else booking confirm page
 *  - host: unreviewed finished stay → host dashboard review modal, else #reservations
 * Anyone else, or a failed lookup, lands on /user-dashboard/trips.
 */
export const reservationProcessingGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const reservationApi = inject(ReservationApiService);
  const router = inject(Router);

  const user = await auth.currentUserOnceLoaded();
  if (!user?.id) {
    return router.createUrlTree(['/auth/login'], { queryParams: { returnTo: state.url } });
  }
  const fallback = router.createUrlTree(['/user-dashboard/trips']);

  const id = Number(route.paramMap.get('id'));
  if (!Number.isInteger(id) || id <= 0) return fallback;

  let booking: IBooking;
  try {
    booking = toBookingView(await firstValueFrom(reservationApi.fetch(id)));
  } catch {
    return fallback;
  }

  if (booking.guestUserId === user.id) {
    return stayIsOver(booking) && !booking.reviewedAt
      ? router.createUrlTree(['/user-dashboard/trips'], { queryParams: { review: booking.id } })
      : router.createUrlTree(['/booking/confirm', booking.id]);
  }
  if (booking.hostUserId === user.id) {
    return stayIsOver(booking) && !booking.hostReviewedAt
      ? router.createUrlTree(['/hosting'], { queryParams: { review: booking.id } })
      : router.createUrlTree(['/hosting'], { fragment: 'reservations' });
  }
  return fallback;
};
